import React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {SOURCES_DIALOG_STACK_PROPS} from './sourcesDialogStackProps';
import {ClassificationMultiSelect, DISTRIBUTION_OPTIONS, COMPARTMENT_OPTIONS} from '../../../../../components/classification/ClassificationFieldsGroup';
import {CLASSIFICATION_LEVELS} from '../../../../../components/prosemirror-editor/helpers/classificationLevels';
import {buildUploadAcceptAttribute, getAllowedUploadExtensions, isAllowedUploadFile} from './sourcesUploadExtensions';
import useSourcesUploadQueueStore from './useSourcesUploadQueueStore';
import {dedupeFiles} from '../../../../../utils/sourceImportHelpers';
import {extractDroppedUploadEntries} from './sourcesDropUtils';

const MAX_FILES = 50;

const DEFAULT_CLASSIFICATION = {level: 'U', distribution: [], compartment: []};

function makeFileClassification(overrides = {}) {
    return {...DEFAULT_CLASSIFICATION, ...overrides};
}

/** Returns the lowercase extension including the dot, or '' if none */
function getExtension(file) {
    const name = String(file?.name || '').trim().toLowerCase();
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? name.slice(dot) : '';
}

/**
 * "Add source: Files" dialog — enlarged to accommodate per-file classification.
 *
 * Each queued file gets its own Classification Level / Distribution / Compartment.
 * The "Apply to all" control at the bottom overwrites every file's classification at once.
 * Unsupported file types surface inline with a clear rejection banner and a
 * "Request support for this format" shortcut per extension.
 */
const SourcesFileImportDialog = ({
    open,
    onClose,
    onSubmitFiles,
    initialFiles = null,
}) => {
    const defaultClassificationLevel = useSourcesUploadQueueStore((state) => state.classificationLevel);

    const [files, setFiles] = React.useState([]);
    const [perFileClassification, setPerFileClassification] = React.useState([]);

    // Files rejected because their extension is unsupported — shape: { file, ext }[]
    const [rejectedFiles, setRejectedFiles] = React.useState([]);
    // Extensions the user has already clicked "Request support" on
    const [requestedExts, setRequestedExts] = React.useState(new Set());

    // "Apply to all" bulk overwrite
    const [bulkLevel, setBulkLevel] = React.useState(defaultClassificationLevel || 'U');
    const [bulkDistribution, setBulkDistribution] = React.useState([]);
    const [bulkCompartment, setBulkCompartment] = React.useState([]);
    const [showBulk, setShowBulk] = React.useState(false);

    const [error, setError] = React.useState(null);

    // Drag-and-drop directly onto the *already-open* dialog (independent from the
    // pre-population `initialFiles` path, which is populated by dropping onto the Sources
    // section before the dialog opens). MUI's Dialog portals to document.body, outside
    // SourcesSectionContent's DOM subtree, so that section's drop handlers never fire for
    // drops on the open dialog — this gives the dialog its own drop target.
    const [isDragOver, setIsDragOver] = React.useState(false);
    const dragDepthRef = React.useRef(0);

    // Pre-populate when opened from drag&drop
    React.useEffect(() => {
        if (open && initialFiles && initialFiles.length) {
            const allowed = initialFiles.filter(isAllowedUploadFile);
            const rejected = initialFiles
                .filter((f) => !isAllowedUploadFile(f))
                .map((f) => ({file: f, ext: getExtension(f)}));
            const deduped = dedupeFiles(allowed).slice(0, MAX_FILES);
            setFiles(deduped);
            setPerFileClassification(deduped.map(() => makeFileClassification({level: defaultClassificationLevel || 'U'})));
            setRejectedFiles(rejected);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialFiles]);

    const resetState = React.useCallback(() => {
        setFiles([]);
        setPerFileClassification([]);
        setRejectedFiles([]);
        setRequestedExts(new Set());
        setBulkLevel(defaultClassificationLevel || 'U');
        setBulkDistribution([]);
        setBulkCompartment([]);
        setShowBulk(false);
        setError(null);
    }, [defaultClassificationLevel]);

    const handleClose = React.useCallback(() => {
        resetState();
        onClose?.();
    }, [onClose, resetState]);

    // Shared file-intake pipeline: allowed/rejected split, dedupe, cap at MAX_FILES, per-file
    // classification seeding, error messaging. Used by both the native file picker input and
    // drag&drop directly onto the open dialog.
    const addIncomingFiles = React.useCallback((incoming) => {
        const selected = Array.from(incoming || []);
        if (!selected.length) return;

        const allowed = selected.filter(isAllowedUploadFile);
        const newRejected = selected
            .filter((f) => !isAllowedUploadFile(f))
            .map((f) => ({file: f, ext: getExtension(f)}));

        if (newRejected.length) {
            setRejectedFiles((prev) => {
                // Dedupe — don't show the same file twice
                const existing = new Set(prev.map((r) => `${r.file.name}:${r.file.size}`));
                const fresh = newRejected.filter((r) => !existing.has(`${r.file.name}:${r.file.size}`));
                return [...prev, ...fresh];
            });
        }

        setFiles((prev) => {
            const combined = dedupeFiles([...prev, ...allowed]);
            const capped = combined.slice(0, MAX_FILES);
            setPerFileClassification((prevCls) => {
                const next = [...prevCls];
                while (next.length < capped.length) {
                    next.push(makeFileClassification({level: defaultClassificationLevel || 'U'}));
                }
                return next.slice(0, capped.length);
            });
            if (combined.length > MAX_FILES) {
                setError(`You can add up to ${MAX_FILES} files at a time. Only the first ${MAX_FILES} were kept.`);
            } else {
                setError(null);
            }
            return capped;
        });
    }, [defaultClassificationLevel]);

    const handleFilesInputChange = React.useCallback((event) => {
        const selected = Array.from(event.target.files || []);
        event.target.value = '';
        addIncomingFiles(selected);
    }, [addIncomingFiles]);

    // Gate on file-carrying drags only — ignore text/element drags so we don't interfere with
    // any other in-dialog drag interaction (none exist today, but keep the guard for safety).
    const isFileDragEvent = React.useCallback((event) => {
        const types = event?.dataTransfer?.types;
        return Array.isArray(types) ? types.includes('Files') : types?.contains?.('Files');
    }, []);

    const resetDragState = React.useCallback(() => {
        dragDepthRef.current = 0;
        setIsDragOver(false);
    }, []);

    const handleDialogDragOver = React.useCallback((event) => {
        if (!isFileDragEvent(event)) return;
        event.preventDefault();
        // Explicitly set dropEffect — some browser/OS combos (real native OS file drags,
        // unlike CDP-synthesized DragEvents) render a "not-allowed" cursor on dragover unless
        // dropEffect is set every time, even though preventDefault() alone is enough to permit
        // the drop programmatically.
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    }, [isFileDragEvent]);

    const handleDialogDragEnter = React.useCallback((event) => {
        if (!isFileDragEvent(event)) return;
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        dragDepthRef.current += 1;
        setIsDragOver(true);
    }, [isFileDragEvent]);

    const handleDialogDragLeave = React.useCallback((event) => {
        if (!isFileDragEvent(event)) return;
        event.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setIsDragOver(false);
        }
    }, [isFileDragEvent]);

    const handleDialogDrop = React.useCallback((event) => {
        if (!isFileDragEvent(event)) return;
        // preventDefault synchronously — dataTransfer items are only valid for the duration of
        // the synchronous event dispatch in some browsers, so capture what we need before the
        // async extractDroppedUploadEntries walk.
        event.preventDefault();
        const dataTransfer = event.dataTransfer;
        resetDragState();

        extractDroppedUploadEntries(dataTransfer)
            .then((entries) => {
                const droppedFiles = entries.map((entry) => entry.file).filter(Boolean);
                if (droppedFiles.length) {
                    addIncomingFiles(droppedFiles);
                }
            })
            .catch((dropError) => {
                console.error('[SourcesFileImportDialog] Failed to process dropped files:', dropError);
                setError(dropError?.message || 'Failed to read dropped files.');
            });
    }, [addIncomingFiles, isFileDragEvent, resetDragState]);

    const handleRemoveFile = React.useCallback((index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setPerFileClassification((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleDismissRejected = React.useCallback((index) => {
        setRejectedFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleRequestSupport = React.useCallback((ext) => {
        setRequestedExts((prev) => new Set([...prev, ext]));
    }, []);

    const handleFileClassificationChange = React.useCallback((index, newClassification) => {
        setPerFileClassification((prev) => {
            const next = [...prev];
            next[index] = newClassification;
            return next;
        });
    }, []);

    const handleApplyToAll = React.useCallback(() => {
        setPerFileClassification((prev) => prev.map(() => ({
            level: bulkLevel,
            distribution: [...bulkDistribution],
            compartment: [...bulkCompartment],
        })));
    }, [bulkLevel, bulkDistribution, bulkCompartment]);

    const handleSubmit = React.useCallback(() => {
        setError(null);
        if (!files.length) {
            setError('Choose at least one file to upload.');
            return;
        }
        onSubmitFiles?.(
            files.map((file, i) => ({
                file,
                classification: perFileClassification[i] || makeFileClassification({level: defaultClassificationLevel || 'U'}),
            }))
        );
        handleClose();
    }, [defaultClassificationLevel, files, handleClose, onSubmitFiles, perFileClassification]);

    const allowedExtensions = React.useMemo(() => getAllowedUploadExtensions(), []);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            {...SOURCES_DIALOG_STACK_PROPS}
            slotProps={{
                ...SOURCES_DIALOG_STACK_PROPS.slotProps,
                paper: {
                    onDragOver: handleDialogDragOver,
                    onDragEnter: handleDialogDragEnter,
                    onDragLeave: handleDialogDragLeave,
                    onDrop: handleDialogDrop,
                    sx: {position: 'relative'},
                },
            }}
        >
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <AttachFileRoundedIcon fontSize="small" color="action"/>
                    <span>Add source: Files</span>
                    <Typography variant="body2" color="text.secondary" sx={{ml: 'auto !important'}}>
                        {files.length > 0 ? `${files.length} file${files.length === 1 ? '' : 's'} selected` : ''}
                    </Typography>
                </Stack>
            </DialogTitle>

            {isDragOver && (
                <Box
                    data-testid="sources-file-dialog-drop-overlay"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        bgcolor: 'rgba(233, 236, 239, 0.5)',
                        backdropFilter: 'blur(4px)',
                        WebkitBackdropFilter: 'blur(4px)',
                        boxShadow: 'inset 0 0 0 2px #1976d2',
                    }}
                >
                    <Box sx={{
                        bgcolor: 'rgba(25, 118, 210, 0.96)',
                        color: 'white',
                        borderRadius: 1,
                        px: 2,
                        py: 1.25,
                        textAlign: 'center',
                        boxShadow: 3,
                    }}>
                        <Typography variant="body2" fontWeight={600}>
                            Drop files to add
                        </Typography>
                    </Box>
                </Box>
            )}

            <DialogContent dividers sx={{minHeight: 420}}>
                <Stack spacing={2}>
                    {/* Description + file picker — explicitly calls out drag&drop support so
                        users don't have to discover it by accident (the highlighted overlay
                        only appears once a drag is already in progress). */}
                    <Box sx={{
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        bgcolor: 'grey.50',
                    }}>
                        <CloudUploadRoundedIcon color="action" sx={{flexShrink: 0}}/>
                        <Stack spacing={0} sx={{flex: 1, minWidth: 0}}>
                            <Typography variant="body2" fontWeight={600}>
                                Drag and drop files anywhere in this window
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Upload up to {MAX_FILES} files (PDF, DOCX, TXT, audio, video, or image).
                                Set a classification per file, or use <strong>Apply to all</strong> to overwrite everything at once.
                            </Typography>
                        </Stack>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<AttachFileRoundedIcon/>}
                            sx={{flexShrink: 0}}
                        >
                            Choose files
                            <input
                                type="file"
                                multiple
                                hidden
                                accept={buildUploadAcceptAttribute()}
                                onChange={handleFilesInputChange}
                            />
                        </Button>
                    </Box>

                    {/* ── Unsupported-format rejections ─────────────────────────────── */}
                    {rejectedFiles.length > 0 && (
                        <Box sx={{
                            border: '1px solid',
                            borderColor: 'warning.light',
                            borderRadius: 1,
                            overflow: 'hidden',
                        }}>
                            {/* Banner */}
                            <Box sx={{
                                px: 2,
                                py: 1,
                                bgcolor: 'warning.50',
                                borderBottom: '1px solid',
                                borderColor: 'warning.light',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}>
                                <BlockRoundedIcon fontSize="small" sx={{color: 'warning.main'}}/>
                                <Typography variant="body2" fontWeight={600} sx={{color: 'warning.dark'}}>
                                    {rejectedFiles.length} unsupported format{rejectedFiles.length === 1 ? '' : 's'} — these files cannot be uploaded
                                </Typography>
                            </Box>

                            {/* One row per rejected file */}
                            {rejectedFiles.map(({file, ext}, index) => {
                                const alreadyRequested = requestedExts.has(ext);
                                return (
                                    <React.Fragment key={`${file.name}-${file.size}-${index}`}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            px: 2,
                                            py: 1,
                                            bgcolor: 'background.paper',
                                        }}>
                                            {/* File info */}
                                            <Stack spacing={0} sx={{flex: 1, minWidth: 0}}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography
                                                        variant="body2"
                                                        noWrap
                                                        title={file.name}
                                                        sx={{fontWeight: 500, color: 'text.secondary'}}
                                                    >
                                                        {file.name}
                                                    </Typography>
                                                    <Chip
                                                        label={ext || 'no extension'}
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                        sx={{fontFamily: 'monospace', fontSize: '0.7rem', flexShrink: 0}}
                                                    />
                                                </Stack>
                                                <Typography variant="caption" color="text.disabled">
                                                    {`${Math.max(1, Math.round(file.size / 1024))} KB · Supported: ${allowedExtensions.join(', ')}`}
                                                </Typography>
                                            </Stack>

                                            {/* Request support / sent confirmation */}
                                            {alreadyRequested ? (
                                                <Chip
                                                    icon={<HelpOutlineRoundedIcon/>}
                                                    label="Support request sent"
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                    sx={{flexShrink: 0}}
                                                />
                                            ) : (
                                                <Tooltip title={`Ask the team to add support for ${ext || 'this format'}`}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="warning"
                                                        startIcon={<HelpOutlineRoundedIcon/>}
                                                        aria-label={`Request support for ${ext || 'this format'}`}
                                                        onClick={() => handleRequestSupport(ext)}
                                                        sx={{whiteSpace: 'nowrap', flexShrink: 0}}
                                                    >
                                                        Request support
                                                    </Button>
                                                </Tooltip>
                                            )}

                                            {/* Dismiss */}
                                            <Tooltip title="Dismiss">
                                                <IconButton
                                                    size="small"
                                                    aria-label="dismiss unsupported file"
                                                    onClick={() => handleDismissRejected(index)}
                                                >
                                                    <CloseRoundedIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        {index < rejectedFiles.length - 1 && <Divider/>}
                                    </React.Fragment>
                                );
                            })}
                        </Box>
                    )}

                    {/* ── Accepted-file table ────────────────────────────────────────── */}
                    {files.length > 0 && (
                        <Box sx={{
                            maxHeight: 480,
                            overflowY: 'auto',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                        }}>
                            {/* Sticky header */}
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 72px 1fr 1fr 36px',
                                gap: 1,
                                px: 2,
                                py: 0.75,
                                bgcolor: 'grey.50',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>File</Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Level</Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Distribution</Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Compartment</Typography>
                                <Box/>
                            </Box>

                            {/* Per-file rows */}
                            {files.map((file, index) => {
                                const cls = perFileClassification[index] || DEFAULT_CLASSIFICATION;
                                const idPrefix = `file-cls-${index}`;
                                return (
                                    <React.Fragment key={`${file.name}-${file.size}-${index}`}>
                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 72px 1fr 1fr 36px',
                                            gap: 1,
                                            px: 2,
                                            py: 1,
                                            alignItems: 'center',
                                            '&:hover': {bgcolor: 'grey.50'},
                                        }}>
                                            {/* File name + size */}
                                            <Stack spacing={0} sx={{minWidth: 0}}>
                                                <Typography variant="body2" noWrap title={file.name} sx={{fontWeight: 500}}>
                                                    {file.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {Math.max(1, Math.round(file.size / 1024))} KB
                                                </Typography>
                                            </Stack>

                                            {/* Level — code only in the table; column header + menu items provide context */}
                                            <FormControl size="small" fullWidth>
                                                <Select
                                                    value={cls.level}
                                                    onChange={(e) => handleFileClassificationChange(index, {...cls, level: e.target.value})}
                                                    input={<OutlinedInput notched={false}/>}
                                                    MenuProps={{sx: {zIndex: 1500}}}
                                                    renderValue={(v) => v}
                                                >
                                                    {CLASSIFICATION_LEVELS.map((lvl) => (
                                                        <MenuItem key={lvl.value} value={lvl.value} sx={{color: lvl.color}}>
                                                            {lvl.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            {/* Distribution — label hidden; column header provides context */}
                                            <ClassificationMultiSelect
                                                idPrefix={`${idPrefix}-dist`}
                                                label="Distribution"
                                                hideLabel
                                                value={cls.distribution}
                                                options={DISTRIBUTION_OPTIONS}
                                                onChange={(v) => handleFileClassificationChange(index, {...cls, distribution: v})}
                                            />

                                            {/* Compartment — label hidden; column header provides context */}
                                            <ClassificationMultiSelect
                                                idPrefix={`${idPrefix}-comp`}
                                                label="Compartment"
                                                hideLabel
                                                value={cls.compartment}
                                                options={COMPARTMENT_OPTIONS}
                                                onChange={(v) => handleFileClassificationChange(index, {...cls, compartment: v})}
                                            />

                                            {/* Remove */}
                                            <Tooltip title="Remove">
                                                <IconButton
                                                    size="small"
                                                    aria-label="remove file"
                                                    onClick={() => handleRemoveFile(index)}
                                                >
                                                    <CloseRoundedIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        {index < files.length - 1 && <Divider/>}
                                    </React.Fragment>
                                );
                            })}
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error">
                            {error}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            {/* ── Apply-to-all footer ──────────────────────────────────────────────── */}
            {files.length > 0 && (
                <>
                    <Divider/>
                    <Box sx={{px: 3, py: 1.5, bgcolor: 'grey.50'}}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{mb: showBulk ? 1.5 : 0}}>
                            <TuneRoundedIcon fontSize="small" color="action"/>
                            <Typography variant="body2" fontWeight={500}>
                                Apply classification to all files
                            </Typography>
                            <Button
                                size="small"
                                variant="text"
                                sx={{ml: 'auto !important', textTransform: 'none'}}
                                onClick={() => setShowBulk((v) => !v)}
                            >
                                {showBulk ? 'Hide' : 'Set for all…'}
                            </Button>
                        </Stack>

                        <Collapse in={showBulk}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <FormControl size="small" sx={{minWidth: 160, flexShrink: 0}}>
                                    <InputLabel id="bulk-level-label">Classification Level</InputLabel>
                                    <Select
                                        labelId="bulk-level-label"
                                        value={bulkLevel}
                                        onChange={(e) => setBulkLevel(e.target.value)}
                                        input={<OutlinedInput label="Classification Level"/>}
                                        MenuProps={{sx: {zIndex: 1500}}}
                                        renderValue={(v) => {
                                            const found = CLASSIFICATION_LEVELS.find((l) => l.value === v);
                                            return found ? found.label : v;
                                        }}
                                    >
                                        {CLASSIFICATION_LEVELS.map((lvl) => (
                                            <MenuItem key={lvl.value} value={lvl.value} sx={{color: lvl.color}}>
                                                {lvl.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Box sx={{flex: 1}}>
                                    <ClassificationMultiSelect
                                        idPrefix="bulk-dist"
                                        label="Distribution"
                                        value={bulkDistribution}
                                        options={DISTRIBUTION_OPTIONS}
                                        onChange={setBulkDistribution}
                                    />
                                </Box>

                                <Box sx={{flex: 1}}>
                                    <ClassificationMultiSelect
                                        idPrefix="bulk-comp"
                                        label="Compartment"
                                        value={bulkCompartment}
                                        options={COMPARTMENT_OPTIONS}
                                        onChange={setBulkCompartment}
                                    />
                                </Box>

                                <Button
                                    variant="contained"
                                    size="small"
                                    color="secondary"
                                    onClick={handleApplyToAll}
                                    sx={{flexShrink: 0, whiteSpace: 'nowrap'}}
                                >
                                    Apply to all
                                </Button>
                            </Stack>
                        </Collapse>
                    </Box>
                </>
            )}

            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={!files.length}>
                    Add {files.length > 0 ? `${files.length} source${files.length === 1 ? '' : 's'}` : 'source'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default React.memo(SourcesFileImportDialog);
